import { useEffect, useState } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   Box,
   Stack,
   Flex,
   Button,
   useToast,
} from '@chakra-ui/react';
import { useLocalization } from '~/framework';
import { CourseAssignmentOptions } from './CourseAssignmentOptions';
import { AssignmentAssigneeInput } from './AssignmentAssigneeInput';
import { AssignmentCourseInput } from './AssignmentCourseInput';
import { AssignmentDeadlineInput } from './AssignmentDeadlineInput';
import { useCreateCourseAssignment } from './useCreateCourseAssignment';

const defaultInitialAssignmentState: CourseAssignmentOptions = {
   wikiid: null,
   assignee_teamid: null,
   assignee_userid: null,
   due_within: null,
};

type CourseAssignmentModalProps = {
   isOpen: boolean;
   onClose: () => void;
   initialAssignmentState?: CourseAssignmentOptions;
};

export function CreateCourseAssignmentModal({
   isOpen,
   onClose,
   initialAssignmentState = defaultInitialAssignmentState,
}: CourseAssignmentModalProps) {
   const { _js } = useLocalization();

   const [assignment, setAssignment] = useState<CourseAssignmentOptions>(initialAssignmentState);

   useEffect(() => {
      setAssignment(initialAssignmentState);
   }, [isOpen]);

   const toast = useToast();

   const createCourseAssignment = useCreateCourseAssignment({
      onSuccess() {
         setAssignment(initialAssignmentState);
         onClose();
         toast({
            title: _js('Assignment Created'),
            status: 'success',
            position: 'top',
            isClosable: true,
         });
      },
   });

   function submitAssignment() {
      return createCourseAssignment.mutate(assignment);
   }

   function abortAssignment() {
      setAssignment(initialAssignmentState);
      onClose();
   }

   const hasAssignee = Boolean(assignment.assignee_teamid || assignment.assignee_userid);

   const hasCourse = Boolean(assignment.wikiid);

   return (
      <Modal isOpen={isOpen} onClose={abortAssignment}>
         <ModalOverlay />
         <ModalContent>
            <ModalHeader>{_js('Assign a Course')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Stack>
                  <Box>
                     <AssignmentAssigneeInput assignment={assignment} onChange={setAssignment} />
                  </Box>
                  <Box display={hasAssignee ? 'block' : 'none'}>
                     <AssignmentCourseInput assignment={assignment} onChange={setAssignment} />
                  </Box>
                  <Box display={hasAssignee && hasCourse ? 'block' : 'none'}>
                     <AssignmentDeadlineInput assignment={assignment} onChange={setAssignment} />
                  </Box>
               </Stack>
            </ModalBody>
            <Flex m={5} justify="space-between">
               <Button onClick={abortAssignment}>{_js('Cancel')}</Button>
               <Button
                  colorScheme="blue"
                  variant="solid"
                  isDisabled={!hasAssignee || !hasCourse || createCourseAssignment.isLoading}
                  onClick={submitAssignment}
               >
                  {_js('Assign Course')}
               </Button>
            </Flex>
         </ModalContent>
      </Modal>
   );
}
